'use client';

import { forwardRef, useState, useRef, useEffect } from 'react';
import { faMinus, faPlus } from '@fortawesome/pro-light-svg-icons';
import { VariantProps, tv } from 'tailwind-variants';

import { Button } from '@/components/ui/button/button.component';
import { AnalyticsEnumKeyType } from '@/types/enums/analytics/analytics.enum';

const titledItemList = tv({
  slots: {
    container: ['w-full flex flex-col text-left bg-white'],
  },
});

export interface TitledItemListProps extends VariantProps<typeof titledItemList> {
  dataTestID?: string;
  listType?: 'bullet' | 'none';
  className?: string;
  analyticsCallback?: () => void;
  items: string[];
  ['data-analytics-event']?: AnalyticsEnumKeyType;
}

export const ItemList = forwardRef<HTMLDivElement, TitledItemListProps>(
  ({ className, 'data-analytics-event': analyticsEventName, items, listType = 'bullet', dataTestID, analyticsCallback }, ref) => {
    const { container } = titledItemList();
    const [showMore, setShowMore] = useState(false);
    const [height, setHeight] = useState<number | null>(null);
    const itemsRef = useRef<HTMLUListElement>(null);
    const maxVisibleItems = 5;

    useEffect(() => {
      if (itemsRef.current) {
        const itemHeight = itemsRef.current.scrollHeight;
        setHeight(showMore ? itemHeight : itemsRef.current.offsetHeight);
      }
    }, [showMore, items]);

    const handleShowMoreToggle = () => {
      if (!showMore) {
        analyticsCallback?.();
      }
      setShowMore((prev) => !prev);
    };

    const visibleItems = showMore ? items : items.slice(0, maxVisibleItems);

    const analyticsEvent = showMore ? analyticsEventName : undefined;

    return (
      <div ref={ref} className={container(className)} data-testid={`${dataTestID}--item-list`}>
        <div
          style={{ height: height ? `${height}px` : 'auto' }}
          className="overflow-hidden transition-all duration-500 ease-in-out"
        >
          <ul ref={itemsRef} className={listType == 'bullet' ? 'list-bullet space-y-1' : 'list-none'}>
            {visibleItems.map((item, index) => (
              <li key={index} className="text-gray-700 break-words whitespace-pre-line">
                {item}
              </li>
            ))}
          </ul>
        </div>
        {items.length > maxVisibleItems && (
          <Button
            variant="text"
            startIcon={showMore ? faMinus : faPlus}
            startIconClassName={`w-[0.875rem] h-[0.875rem] transition-transform duration-300 ease-in-out ${
              showMore ? 'rotate-180' : 'rotate-0'
            }`}
            className="mt-2 text-green-500 hover:underline focus:outline-none transition-colors duration-300 ease-in-out text-left justify-start"
            data-testid={`${dataTestID}-show-more-button--professional-about`}
            data-analytics-event={analyticsEvent}
            onClick={handleShowMoreToggle}
          >
            {showMore ? 'Recolher' : 'Ver mais'}
          </Button>
        )}
      </div>
    );
  },
);

ItemList.displayName = 'TitledItemList';
